import { merge } from 'lodash';
import { select, selectAll, zoom, zoomTransform, zoomIdentity, scaleSqrt, nest, event as d3event, mouse as d3mouse } from "d3c";
import { geoPath, geoMercator } from "d3-geo";
import GeomapVectorTiles from './geomap_tiles';
import GeomapBubbles from './geomap_bubbles';
import GeomapArcs from './geomap_arcs';
import GeomapCountries from './geomap_countries';
import GeomapPolygons from './geomap_polygons';

export default class GeoMap {
	constructor() {
		// Default options
		this.options = {
			wrapper: 'wrapper',
			controlsWrapper: null, // contol buttons will be appended to this element
			translations: null, // translation dictionary, if not provided - default english words are used
			drawArcsOnInit: true,
			maxZoom: 9500,
			updateColor: '#a8a8a8', // @portal-color-updating
			// Map is restricted to rectangle with these coordinates
			mapBoundaries: [[-179.9, 84.1], [179.9, -84.1]],
			animationDuration: 800,
			countriesConfig: {
				// config for country layer
				url: './world.json',
				scope: 'world',
				hideAntarctica: true,
				highlightOnHover: true,
				highlightFillColor: '#a8a8a8', // @portal-color-gray-light
				highlightFillOpacity: 0,
				highlightBorderWidth: 1,
				highlightBorderColor: 'rgba(84, 84, 84, 1)', // @portal-color-gray-dark
				highlightLinks: false // highlight country arc on hover
			},
			bubblesConfig: {
				highlightLinks: true,
				borderWidth: 1,
				highlightBorderWidth: 1,
				fillOpacity: 0.33, // @vis-node-opacity
				highlightFillOpacity: 0.66, // @vis-node-opacity-hover
				borderOpacity: 1
			},
			arcsConfig: {
				// draw either lines or shapes (shapes will be filled, lines stroked)
				drawLines: false,
				// coloring type (possible values: 'solid', 'gradient')
				colorType: 'gradient',
				// control the appearance of the line (possible values: straight, elliptical, 90straight, 90+90straight, 90+90curved)
				lineType: 'elliptical',
				// control the appearance of the shape (possible values: straight, elliptical, elliptical+mirrored, curved+balanced, curved+balanced+mirroredH, curved+balanced+mirroredV, flow)
				shapeType: 'elliptical+mirrored',
				invertDirection: true,
				gradientOpaque: false,
				gradientOpaqueColor: '#a8a8a8', // @portal-color-gray-light
				strokeWidth: 1,
				fillOpacity: 0.125,
				highlightFillOpacity: 0.5,
				strokeOpacity: 0.125,
				highlightStrokeOpacity: 0.5,
				animationSpeed: 1000,
				showWeight: false // switch between value-weighted and count-based arc thickness
			},
			tilesConfig: {
				url: null,
				extension: '.pbf',
				showContributors: true,
				language: 'en',
				minZoomScaleForCountryLabels: 2,
				countryLabelColor: '#545454', // @portal-color-gray-dark
				cityLabelColor: '#a8a8a8' // @portal-color-gray-light
			},
			polygonsConfig: {
				primaryColor: '#99794c',
				primaryOpacity: 0.4,
				pointRadius: 3,
				pointOpacity: 0.8
			},
			defaultFocus: {
				k: 0,
				x: 0,
				y: 0
			}
		};

		this._state = {
			colorMode: 'sentiment',
			marker: false, // Cynosure marker
			minZoom: 1,
			path: null,
			projection: null,
			projectionForLabels: null, // Labels need own projection due to zooming issues
			previousTransform: null,
			radius: null, // function to normalize bubbles and arcs size
			bubblesShown: true,
			_data: {
				sources: [],
				targets: [],
				links: []
			}
		};

		this._layers = {};
	}

	initialize(options, initCallback) {
		this.options = merge(this.options, options);
		this.legend = options.legend;
		const d3element = select(this.options.wrapper);
		this.options.element = d3element.node();
		this._state.arcsShown = this.options.drawArcsOnInit;
		this.svgContainer = d3element
			.classed('GeographicMapContent', true)
			.append('svg')
			.classed('datamap', true);

		this.svgContainer.append('g').attr('class', 'geomap_zoomer');

		if (this.legend) {
			this.legend
				.on('highlightNodes', (container, count) => {
					if (this.options.wrapper === container) {
						const bubblesConfig = this.options.bubblesConfig;
						this.svgContainer
							.selectAll('.datamaps-bubbleGroup')
							.each(function() {
								const bubbles = select(this).selectAll('.datamaps-bubbleFg');
								bubbles.style('fill-opacity', (d, i) => {
									const hasSameCount = count === d.frequency
										? bubblesConfig.highlightFillOpacity
										: bubblesConfig.fillOpacity;
									return i === bubbles.size() - 1 && bubbles.size() > 1 ? 1 : hasSameCount;
								}
								);
							});
					}
				})
				.on('dehighlightNodes', () => {
					this.dehighlightBubbles(this._state);
				});
		}

		// set projections and paths based on scope
		const pathAndProjection = _setProjection(this.options.element);
		this._state.path = pathAndProjection.path;
		this._state.projection = pathAndProjection.projection;
		this._state.projectionForLabels = _setProjection(this.options.element).projection;

		const zoomed = zoomHandler.bind(this);
		this.zoomv5 = zoom()
			.scaleExtent([this._state.minZoom, this.options.maxZoom])
			.on('zoom', zoomed);
		this.svgContainer.call(this.zoomv5);
		this._state.previousTransform = zoomTransform(this.svgContainer.node());
		this._updateProjections();

		this._initializeInterface();
		this._initLayers().then(() => {
			this.initialized = true;

			// initial panning to fit map into container size
			this.panTo(this.options.defaultFocus);
			typeof initCallback === 'function' && initCallback();
		});
		return this;
	}

	_initLayers() {
		this._layers.vectorTiles = new GeomapVectorTiles(this.options);
		this._layers.polygons = new GeomapPolygons(this.options);
		this._layers.countries = new GeomapCountries(this.options, this._state.path);
		this._layers.arcs = new GeomapArcs(this.options);
		this._layers.bubbles = new GeomapBubbles(this.options);
		return Promise.all(_.map(this._layers, layer => layer.init())).then(() => {
			this._initLayersListeners();
		});
	}

	_initLayersListeners() {
		const _this = this;
		// Bubbles listeners
		this._layers.bubbles.on('mouseover', function(d) {
			d3event.stopPropagation();
			// check if a button is currently being pressed on the mouse
			if (d3event.buttons !== 0) {
				return;
			}
			d.specific = 'bubble';

			const shape = _this._layers.countries.getShape(d.latitude, d.longitude);
			if (shape !== null) {
				if (!d.country) {
					d.country = {};
				}
				d.country.properties = shape.properties;
			}

			_this.observer.notify('bubble.mouseover', d3event, d);

			if (_this.legend && _this.legend.initialized && !d.statistics) {
				_this.legend.showNode(d.frequency, _this._state.radius);
			}
		});
		this._layers.bubbles.on('mousemove', function(d) {
			d3event.stopPropagation();
			// check if a button is currently being pressed on the mouse
			if (d3event.buttons !== 0) {
				return;
			}
			d.specific = 'bubble';
			_this.observer.notify('bubble.mousemove', d3event, d);
		});
		this._layers.bubbles.on('mouseout', function(d) {
			// check if a button is currently being pressed on the mouse
			if (d3event.buttons !== 0) {
				return;
			}
			_this.observer.notify('bubble.mouseout', d3event, d);

			if (_this.legend && _this.legend.initialized) {
				_this.legend.hideNode();
			}
		});
		this._layers.bubbles.on('click', function(d) {
			if (d.defaultPrevented) {
				return; // dragged
			}
			d3event.stopPropagation();
			d.specific = 'bubble';
			_this.observer.notify('bubble.click', d3event, d);
		});
		// Country listeners
		this._layers.countries.on('mouseover', function(d) {
			// check if a button is currently being pressed on the mouse
			if (d3event.buttons !== 0) {
				return;
			}
			_this.observer.notify('country.mouseover', d3event, d);
		});
		this._layers.countries.on('mouseout', function(d) {
			// check if a button is currently being pressed on the mouse
			if (d3event.buttons !== 0) {
				return;
			}
			_this.observer.notify('country.mouseout', d3event, d);
		});
		this._layers.countries.on('click', function(d) {
			if (d3event.defaultPrevented) {
				return; // dragged
			}
			const mousePos = d3mouse(this);
			d3event.stopPropagation();
			d.mousePos = mousePos;
			d.specific = 'country';
			_this.observer.notify('country.click', d3event, d);
		});
	}

	_initializeInterface() {
		this.options.element.style.overflow = 'hidden';
		if (!this.options.controlsWrapper) {
			this.options.controlsWrapper = this.options.wrapper;
		}
		const controls = select(this.options.controlsWrapper);
		controls
			.append('div')
			.attr('class', 'control-button icon-focus control-focus')
			.attr('title', this.options.translations ? this.options.translations('Focus') : 'Focus')
			.on('click', () => {
				this.panTo(this.options.defaultFocus);
			});
		controls
			.append('div')
			.attr('class', 'control-button icon-layer-remove control-zoom-out')
			.attr('title', this.options.translations ? this.options.translations('Zoom-out') : 'Zoom-out')
			.on('click', () => {
				this.manualzoom(false);
			});
		controls
			.append('div')
			.attr('class', 'control-button icon-layer-add control-zoom-in')
			.attr('title', this.options.translations ? this.options.translations('Zoom-in') : 'Zoom-in')
			.on('click', () => {
				this.manualzoom(true);
			});
	}

	_updateProjections() {
		this.svgContainer.attr('width', this.options.element.offsetWidth).attr('height', this.options.element.offsetHeight);
		this._state.projection
			.scale(128 / 2 / Math.PI)
			.translate([0, 0])
			// Clip extent to fix mercator projection +infinity problem on poles
			.clipExtent([
				[
					(-this.options.element.offsetWidth + 1) / 2,
					(-this.options.element.offsetHeight + 1) / 2
				], [
					(this.options.element.offsetWidth - 1) / 2,
					(this.options.element.offsetHeight - 1) / 2
				]
			]);
		this._state.projectionForLabels.translate([0, 0]);
		this._updateMinZoom();
	}

	// Recalculates minimal zoom according to current container dimensions
	_updateMinZoom() {
		const topLeft = this._state.projection(this.options.mapBoundaries[0]);
		const bottomRight = this._state.projection(this.options.mapBoundaries[1]);
		const projectionAspectRatio = (bottomRight[0] - topLeft[0]) / (bottomRight[1] - topLeft[1]);
		const wrapperWidth = this.options.element.offsetWidth;
		const wrapperHeight = this.options.element.offsetHeight;
		const wrapperAspectRatio = wrapperWidth / wrapperHeight;
		// Calculate minZoom according to wrapper and mapBoundaries sizes
		this._state.minZoom =
			wrapperAspectRatio >= projectionAspectRatio
				? wrapperWidth / (bottomRight[0] - topLeft[0])
				: wrapperHeight / (bottomRight[1] - topLeft[1]);
		this.zoomv5.scaleExtent([this._state.minZoom, this.options.maxZoom]);
	}

	// Size of wrapper was changed
	updateSize() {
		if (!this.initialized) {
			return;
		}
		const oldWidth =
			this._state.previousTransform.k *
			(this.viewportBoundaryCoordinates[1][0] - this.viewportBoundaryCoordinates[0][0]);
		this._updateProjections();
		this._layers.vectorTiles && this._layers.vectorTiles.updateContainerSize();
		const zoomMultiplicator = oldWidth !== 0 ? this.options.element.offsetWidth / oldWidth : 1;
		this.svgContainer.call(
			this.zoomv5.transform,
			zoomIdentity
				.translate(
					this._state.previousTransform.x * zoomMultiplicator,
					this._state.previousTransform.y * zoomMultiplicator
				)
				.scale(Math.max(this._state.previousTransform.k * zoomMultiplicator, this._state.minZoom))
		);
		this.update();
	}

	toggleContributors(enable) {
		this.svgContainer
			.selectAll('.geomap-contributors')
			.classed('hidden', !enable);
	}

	updateArcsType(typeId) {
		if (typeId === 1) {
			this.options.arcsConfig.drawLines = false;
			this.options.arcsConfig.lineType = 'elliptical';
			this.options.arcsConfig.shapeType = 'elliptical+mirrored';
			this.options.arcsConfig.invertDirection = true;
			this.options.arcsConfig.colorType = 'gradient';
			this.options.arcsConfig.gradientOpaque = false;
		} else if (typeId === 2) {
			this.options.arcsConfig.drawLines = false;
			this.options.arcsConfig.lineType = 'elliptical';
			this.options.arcsConfig.shapeType = 'elliptical';
			this.options.arcsConfig.invertDirection = true;
			this.options.arcsConfig.colorType = 'gradient';
			this.options.arcsConfig.gradientOpaque = false;
		} else if (typeId === 3) {
			this.options.arcsConfig.drawLines = true;
			this.options.arcsConfig.lineType = 'straight';
			this.options.arcsConfig.shapeType = 'elliptical';
			this.options.arcsConfig.invertDirection = false;
			this.options.arcsConfig.colorType = 'solid';
		} else if (typeId === 4) {
			this.options.arcsConfig.drawLines = true;
			this.options.arcsConfig.lineType = '90+90straight';
			this.options.arcsConfig.shapeType = 'elliptical+mirrored';
			this.options.arcsConfig.invertDirection = true;
			this.options.arcsConfig.colorType = 'solid';
			this.options.arcsConfig.gradientOpaque = false;
		} else {
			throw new Error(`Arcs type '${typeId}' is not supported`);
		}
		this._layers.arcs.update(this._state);
	}

	updateBubbleSize(factor) {
		this._layers.bubbles.updateBubbleSize(factor, this._state);
		this.legend &&
			this.legend.update(this.options.element.offsetWidth, this.options.element.offsetHeight, this._state.radius);
	}

	/**
	 * Update data on geomap
	 */
	update(withAnimation, animationCallback) {
		if (!this.initialized) {
			return;
		}
		!this.forcedLegend && this._updateLegend(this._state._data.targets);
		_.forEach(this._layers, layer => layer.update && layer.update(this._state, withAnimation, animationCallback));

		// Empty dataset
		if (this._state._data.targets.length === 0 && !this.forcedLegend) {
			this.legend && this.legend.hide();
		}
		return this;
	}

	autozoom() {
		if (this._state.marker) {
			const autozoomTransform = this._getAutozoomTransform(this._layers.bubbles, this._state.marker);
			const scale = autozoomTransform && autozoomTransform.scale ? autozoomTransform.scale : 23;
			this.panTo({
				k: scale,
				x: this._state.marker[0],
				y: this._state.marker[1]
			});
		} else {
			this._autozoom(this._layers.bubbles);
		}
	}

	_autozoom(layer) {
		const autozoomTransform = this._getAutozoomTransform(layer);
		if (!autozoomTransform) {
			return;
		}
		this.svgContainer.call(
			this.zoomv5.transform,
			zoomIdentity.translate(autozoomTransform.offset[0], autozoomTransform.offset[1]).scale(autozoomTransform.scale)
		);
	}

	_getAutozoomTransform(layer, marker) {
		if (layer) {
			this.featuresBox = layer.getBounds(this._state);
		}
		if (!this.featuresBox) {
			return null;
		}

		const canvas = {
			width: this.options.element.offsetWidth,
			height: this.options.element.offsetHeight
		};
		canvas.aspectratio = canvas.width / canvas.height;
		canvas.center = [canvas.width / 2, canvas.height / 2];

		const margin = 1.1;
		let vis;
		if (marker) {
			// With marker set correction is needed, because map have to be centered at marker coords
			const projectedMarker = this._state.projection(marker);
			vis = {
				width:
					Math.max(
						Math.abs(this.featuresBox[0][0] - projectedMarker[0]),
						Math.abs(this.featuresBox[1][0] - projectedMarker[0])
					) *	2 *	margin,
				height:
					Math.max(
						Math.abs(this.featuresBox[0][1] - projectedMarker[1]),
						Math.abs(this.featuresBox[1][1] - projectedMarker[1])
					) *	2 *	margin
			};
		} else {
			vis = {
				width: (this.featuresBox[1][0] - this.featuresBox[0][0]) * margin,
				height: (this.featuresBox[1][1] - this.featuresBox[0][1]) * margin
			};
		}
		vis.aspectratio = vis.width / vis.height;
		vis.offsetMarginAdaptation = [
			this.featuresBox[0][0] - (vis.width - (this.featuresBox[1][0] - this.featuresBox[0][0])) / 2,
			this.featuresBox[0][1] - (vis.height - (this.featuresBox[1][1] - this.featuresBox[0][1])) / 2
		];
		vis.offset = [
			vis.offsetMarginAdaptation[0] + (vis.width / 2),
			vis.offsetMarginAdaptation[1] + (vis.height / 2)
		];

		let scale;
		const currentScaleExtent = this.zoomv5.scaleExtent();

		if (canvas.aspectratio > vis.aspectratio) {
			scale = canvas.height / vis.height;
		} else {
			scale = canvas.width / vis.width;
		}
		scale = Math.max(scale, currentScaleExtent[0]);
		scale = Math.min(scale, currentScaleExtent[1]);

		const offset = [
			canvas.center[0] - (vis.offset[0] * scale),
			canvas.center[1] - (vis.offset[1] * scale)
		];
		const latlng = this._state.projection.invert(vis.offset);

		if (scale <= this.zoomv5.scaleExtent()[0]) {
			offset[0] = this.options.element.offsetWidth / 2;
			offset[1] = this.options.element.offsetHeight / 1.65;
		}

		return {
			scale,
			offset,
			latlng
		};
	}

	manualzoom(zoomIn) {
		const factor = 1.25;
		this.svgContainer.call(this.zoomv5.scaleBy, zoomIn ? factor : 1 / factor);
		return this;
	}

	panTo(focus) {
		if (!this.initialized) {
			throw new Error('Geomap should be initialized');
		}
		const currentFocus = focus || this.options.defaultFocus;
		currentFocus.k = currentFocus.k || zoomTransform(this.svgContainer.node()).k;
		const width = this.options.element.offsetWidth;
		const height = this.options.element.offsetHeight;
		let scale = currentFocus.k;
		scale = Math.max(scale, this._state.minZoom);
		scale = Math.min(scale, this.options.maxZoom);
		const translate = this._state.projection([currentFocus.x, currentFocus.y]);
		const x0y0 = [width / 2, height / 2];
		const offset = [x0y0[0] - translate[0] * scale, x0y0[1] - translate[1] * scale];
		this.svgContainer.call(this.zoomv5.transform, zoomIdentity.translate(offset[0], offset[1]).scale(scale));
		return this;
	}

	setForcedLegend(valuesExtent) {
		this.forcedLegend = valuesExtent;
		valuesExtent && this._updateLegend(null, valuesExtent);
	}

	// Adds legend (scale indicator and contributors) to Geomap
	// if valuesExtent is provided - use it as [min, max] values. Otherwise - calculate based on _data
	_updateLegend(_data, valuesExtent) {
		let minValue = valuesExtent
			? valuesExtent[0]
			: _data.reduce(function(p, c) {
					const r = c.frequency;
					return Math.min(p, r);
			  }, Number.POSITIVE_INFINITY);

		const maxValue = valuesExtent
			? valuesExtent[1]
			: _data.reduce(function(p, c) {
					const r = c.frequency;
					return Math.max(p, r);
			  }, Number.NEGATIVE_INFINITY);

		const wrapperWidth = this.options.element.offsetWidth;
		const wrapperHeight = this.options.element.offsetHeight;
		const minRadius = Math.max(1, wrapperWidth / 500);
		const maxRadius = wrapperWidth / 50;
		minValue = Math.min(minValue, maxValue - 1);

		this._state.radius = scaleSqrt()
			.domain([minValue, maxValue])
			.range([minRadius, maxRadius]);

		if (maxValue === Number.NEGATIVE_INFINITY || minValue === Number.POSITIVE_INFINITY) {
			return this;
		}

		if (this.legend) {
			// initialize and update the legend
			if (!this.legend.initialized) {
				this.legend.initialize(
					this.options.element.id,
					this.svgContainer,
					'int',
					this.options.translations ? this.options.translations('Documents') : 'Documents'
				);
			}
			if (wrapperWidth !== 0 && wrapperHeight !== 0) {
				this.legend.update(wrapperWidth, wrapperHeight, this._state.radius);
			}
		}

		return this;
	}

	highlightLocations(locations) {
		const bubbles = this.svgContainer.selectAll('.datamaps-bubbleFg');
		this.dehighlightBubbles(this._state);

		if (locations) {
			const filteredBubbles = bubbles.filter(function(d) {
				return _.some(locations, function(location) {
					return d.latitude === location.latitude && d.longitude === location.longitude;
				});
			});
			if (filteredBubbles.size() > 0) {
				this.highlightBubbles(filteredBubbles);
			}
		}

		return this;
	}

	XYtoLonLat(coords) {
		return this._state.projection.invert(coords);
	}

	// Place cynosure marker
	setMarker(coords) {
		this.svgContainer
			.selectAll('.datamap-marker')
			.remove();

		this._state.marker = coords;
		if (!this._state.marker) {
			return this;
		}

		const xy = this._state.projection(coords);
		const canvas = this.svgContainer.select('.geomap_zoomer');

		const markerElement = canvas.append('svg:g').attr('class', 'datamap-marker');

		markerElement
			.append('circle')
			.attr('class', 'datamap-marker-c')
			.style('stroke', '#000000') // @portal-color-black
			.style('stroke-width', 1 / this._state.previousTransform.k)
			.style('fill', 'none')
			.attr('cx', xy[0])
			.attr('cy', xy[1])
			.attr('data-radius', '8')
			.attr('r', 8 / this._state.previousTransform.k);

		markerElement
			.append('circle')
			.attr('class', 'datamap-marker-c')
			.style('stroke', '#000000') // @portal-color-black
			.style('stroke-width', 1 / this._state.previousTransform.k)
			.style('fill', 'none')
			.attr('cx', xy[0])
			.attr('cy', xy[1])
			.attr('data-radius', '5')
			.attr('r', 5 / this._state.previousTransform.k);

		return this;
	}

	setLanguage(lang) {
		this.options.tilesConfig.language = lang;
	}

	setUpdatingState(updating) {
		this._layers.bubbles && this._layers.bubbles.setUpdatingState(updating);
		this._layers.arcs && this._layers.arcs.setUpdatingState(updating);

		return this;
	}

	data(data) {
		if (!data) {
			return this._state._data;
		}
		const aggregatedTargets = nest()
			.key(d => `${d.latitude}/${d.longitude}`)
			.sortValues((a, b) => b.frequency - a.frequency)
			.entries(data.targets);

		aggregatedTargets.sort((a, b) => b.values[0].frequency - a.values[0].frequency);

		this._state._data = { ...data, aggregatedTargets };
		return this;
	}

	colorMode(colorMode) {
		if (!colorMode) {
			return this._state.colorMode;
		}
		this._state.colorMode = colorMode;
		return this;
	}

	highlightCountry(elem) {
		const countryShape = select(elem);
		if (this.options.countriesConfig.highlightOnHover) {
			this._layers.countries.highlightCountryBorder(countryShape);
		}
		if (this.options.countriesConfig.highlightLinks) {
			this._layers.arcs.highlightCountryArcs(countryShape.data()[0].properties.iso2);
		}
	}

	dehighlightCountries() {
		this._layers.countries.dehighlightCountryBorders();
		this._layers.arcs.dehighlightArcs();
	}

	/**
	 * @param elem - css selector or d3 selection
	 */
	highlightBubbles(elem) {
		const d3elem = typeof elem === 'string' ? selectAll(elem) : elem;
		d3elem
			.style('stroke-width', this.options.bubblesConfig.highlightBorderWidth / this._state.previousTransform.k)
			.style('fill-opacity', (d, i) =>
				i === d3elem.size() - 1 && d3elem.size() > 1 ? 1 : this.options.bubblesConfig.highlightFillOpacity
			);

		// show bubble arcs
		if (this.options.bubblesConfig.highlightLinks) {
			this._layers.arcs.highlightBubbleArcs(d3elem.data());
		}
	}

	dehighlightBubbles() {
		this._layers.bubbles.dehighlightBubbles(this._state);
		this._layers.arcs.dehighlightArcs();
	}

	drawPolygons(geojsonFeatures, autozoom) {
		if (this._layers.polygons) {
			this._layers.polygons.clearLayer();
			this._layers.polygons.drawPolygons(
				geojsonFeatures,
				function() {
					if (autozoom) {
						this._autozoom(this._layers.polygons);
					}
				}.bind(this)
			);
		}
	}

	clearPolygons() {
		if (this._layers.polygons) {
			this._layers.polygons.clearLayer();
		}
	}

	toggleArcs() {
		this._state.arcsShown = !this._state.arcsShown;
		this._layers.arcs.enable(this._state.arcsShown);
		this._layers.arcs.update(this._state);
	}

	toggleBubbles() {
		this._state.bubblesShown = !this._state.bubblesShown;
		this._layers.bubbles.enable(this._state.bubblesShown);
		this._layers.bubbles.update(this._state, true);
	}

	getCurrentViewport() {
		return parseViewport({
			topLeft: this._state.projection
				.invert([this.viewportBoundaryCoordinates[0][0], this.viewportBoundaryCoordinates[0][1]])
				.map(el => +el.toFixed(3)),
			bottomRight: this._state.projection
				.invert([this.viewportBoundaryCoordinates[1][0], this.viewportBoundaryCoordinates[1][1]])
				.map(el => +el.toFixed(3))
		});
	}

	drawRect(topLeft, bottomRight) {
		this._layers.polygons && this._layers.polygons.drawRect(topLeft, bottomRight);
	}

	drawCircle(point, radius) {
		this._layers.polygons && this._layers.polygons.drawCircle(point, radius);
	}

	clearRects() {
		this._layers.polygons && this._layers.polygons.clearRects();
	}
}

function zoomHandler() {
	const currentZoomTransform = d3event.transform;
	const currentZoomScale = d3event.transform.k;
	const isZoomChanged = this._state.previousTransform.k !== currentZoomScale;
	// Check zooming out of borders
	const topLeft = this._state.projection(this.options.mapBoundaries[0]);
	const bottomRight = this._state.projection(this.options.mapBoundaries[1]);
	const projectionBorders = [
		[
			topLeft[0] * currentZoomTransform.k + currentZoomTransform.x,
			topLeft[1] * currentZoomTransform.k + currentZoomTransform.y
		],
		[
			bottomRight[0] * currentZoomTransform.k + currentZoomTransform.x,
			bottomRight[1] * currentZoomTransform.k + currentZoomTransform.y
		]
	];
	const wrapperWidth = this.options.element.offsetWidth;
	const wrapperHeight = this.options.element.offsetHeight;
	if (projectionBorders[0][0] > 0) {
		currentZoomTransform.x -= projectionBorders[0][0];
	} else if (projectionBorders[1][0] < wrapperWidth) {
		currentZoomTransform.x += wrapperWidth - projectionBorders[1][0];
	} else {
		// Current map viewport horizontally falls into window boundaries
	}
	if (projectionBorders[0][1] > 0) {
		currentZoomTransform.y -= projectionBorders[0][1];
	} else if (projectionBorders[1][1] < wrapperHeight) {
		currentZoomTransform.y += wrapperHeight - projectionBorders[1][1];
	} else {
		// Current map viewport vertically falls into window boundaries
	}

	// To handle non-existing tiles
	// Args: (current transform, current scale)
	const errorCallback = () => {
		// Try to zoomOut until tiles can be fetched
		this.svgContainer.call(this.zoomv5.scaleBy, 0.5);
	};

	if (this._state.marker) {
		this.svgContainer
			.selectAll('.datamap-marker-c')
			.style('stroke-width', 1 / currentZoomTransform.k)
			.attr('r', function() {
				return +this.getAttribute('data-radius') / currentZoomTransform.k;
			});
	}
	this.svgContainer
		.select('.geomap_zoomer')
		.attr('transform', currentZoomTransform);

	_.forEach(
		this._layers,
		layer => layer.zoomed && layer.zoomed(this._state, currentZoomTransform, isZoomChanged, errorCallback)
	);

	// Find coordinates of visible viewport
	this.viewportBoundaryCoordinates = [
		[
			-currentZoomTransform.x / currentZoomTransform.k,
			-currentZoomTransform.y / currentZoomTransform.k
		],
		[
			(wrapperWidth - currentZoomTransform.x) / currentZoomTransform.k,
			(wrapperHeight - currentZoomTransform.y) / currentZoomTransform.k
		]
	];

	if (JSON.stringify(currentZoomTransform) !== JSON.stringify(this._state.previousTransform)) {
		this.observer.notify('viewport.update', this.getCurrentViewport());
	}

	this._state.previousTransform = currentZoomTransform;
}

const _setProjection = element => {
	const projection = geoMercator()
		.scale(Math.min(element.offsetHeight, element.offsetWidth) / 2 / Math.PI)
		.translate([element.offsetWidth / 2, element.offsetHeight / 2])
		.precision(0.1);

	const path = geoPath().projection(projection);

	return {
		path,
		projection
	};
}

const parseViewport = viewport => {
	if (viewport) {
		// Swap lon and lat, because d3 and es have different coordinate representation
		viewport.topLeft = viewport.topLeft.reverse();
		viewport.bottomRight = viewport.bottomRight.reverse();
	}
	return viewport;
};
